import type { TranslationKey } from '@change-corgi/core/i18n';
import { translationKey } from '@change-corgi/core/i18n';
import type { Utilities, UtilityContext } from '@change-corgi/core-react/utilityContext';

export type PolicySlug =
	| 'policies'
	| 'applicants'
	| 'terms'
	| 'privacy'
	| 'cookies'
	| 'community'
	| 'fundraising-terms';

const SLUG_MAPPING: Record<string, PolicySlug> = {
	applicants: 'applicants',
	'terms-of-service': 'terms',
	privacy: 'privacy',
	cookies: 'cookies',
	community: 'community',
	'fundraising-terms': 'fundraising-terms',
};

export function getPolicySlugFromRouteSlug(slug: string | undefined): PolicySlug | undefined {
	if (!slug) return 'policies';
	return SLUG_MAPPING[slug];
}

type Config = {
	heroImageUrl: string;
	showUpdateDate?: boolean;
	viewEventName: string;
} & ({ titleKey: TranslationKey } | { title: string });

const CONFIG: Record<PolicySlug, Config> = {
	applicants: {
		heroImageUrl: '/brand-pages/hero%20images/hero-privacy-policy.jpg',
		titleKey: translationKey('fe.pages.applicants.title'),
		showUpdateDate: true,
		viewEventName: 'applicants_policy_view',
	},
	policies: {
		heroImageUrl: '/brand-pages/hero%20images/hero-policies.jpg',
		titleKey: translationKey('fe.pages.policies.title'),
		viewEventName: 'policies_view',
	},
	terms: {
		heroImageUrl: '/brand-pages/hero%20images/hero-terms-of-service.jpg',
		titleKey: translationKey('fe.pages.terms_of_service.title'),
		showUpdateDate: true,
		viewEventName: 'terms_of_service_view',
	},
	privacy: {
		heroImageUrl: '/brand-pages/hero%20images/hero-privacy-policy.jpg',
		titleKey: translationKey('fe.pages.privacy.title'),
		showUpdateDate: true,
		viewEventName: 'privacy_policy_view',
	},
	cookies: {
		heroImageUrl: '/brand-pages/hero%20images/hero-cookie.jpg',
		titleKey: translationKey('fe.pages.cookies.title'),
		showUpdateDate: true,
		viewEventName: 'cookie_policy_view',
	},
	community: {
		heroImageUrl: '/brand-pages/hero%20images/hero-community-guidelines.jpg',
		titleKey: translationKey('fe.pages.community_guidelines.title'),
		showUpdateDate: true,
		viewEventName: 'community_policy_view',
	},
	'fundraising-terms': {
		heroImageUrl: '/brand-pages/hero%20images/hero-fundraising-terms.jpg',
		title: 'Fundraising Terms',
		showUpdateDate: true,
		viewEventName: 'fundraising_terms_view',
	},
};

export function getTitle({ i18n }: Pick<UtilityContext, 'i18n'> | Pick<Utilities, 'i18n'>, slug: PolicySlug): string {
	const config = CONFIG[slug];
	if ('title' in config) {
		return config.title;
	}
	return i18n.typedTranslate(config.titleKey);
}

export function getHeroImageUrl(slug: PolicySlug): string {
	return CONFIG[slug].heroImageUrl;
}

export function isUpdateDateVisible(slug: PolicySlug): boolean {
	return !!CONFIG[slug].showUpdateDate;
}

export function getViewEventName(slug: PolicySlug): string {
	return CONFIG[slug].viewEventName;
}
